import { Grid, type TextFieldPropsSizeOverrides } from '@mui/material';
import { type OverridableStringUnion } from '@mui/types';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useContext } from 'react';

import TimeAutocomplete from './TimeAutocomplete';

import CurrentUserContext from '@/Contexts/CurrentUser';
import {
  getEndTimeOptionsAfterStart,
  isSameDayTimeRangeInverted,
  minutesOfDayFromDateTime,
} from '@/utils/dateTimeHelpers';

export interface TimeScheduleState {
  startTime: Dayjs | null;
  endTime: Dayjs | null;
}

interface TimeScheduleSectionProps {
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  onChange: (field: 'startTime' | 'endTime', value: Dayjs | null) => void;
  size?: OverridableStringUnion<'small' | 'medium', TextFieldPropsSizeOverrides>;
}

const TimeScheduleSection: React.FC<TimeScheduleSectionProps> = ({ startTime, endTime, onChange, size }) => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const endTimeOptions = getEndTimeOptionsAfterStart(startTime);

  const isRangeInverted = isSameDayTimeRangeInverted(startTime, endTime);
  const isOvernight = Boolean(
    startTime &&
    endTime &&
    (endTime.isAfter(startTime, 'day') || minutesOfDayFromDateTime(endTime) < minutesOfDayFromDateTime(startTime)),
  );

  const formatEndTimeLabel = (option: string) => {
    const formatted = dayjs(option, 'HH:mm').format(dateTimePreference.time);
    if (!startTime) return formatted;
    const optMinutes = minutesOfDayFromDateTime(dayjs(option, 'HH:mm'));
    const startMinutes = minutesOfDayFromDateTime(startTime);
    return optMinutes < startMinutes ? `${formatted} (+1 day)` : formatted;
  };

  const handleTimeUpdate = (field: 'startTime' | 'endTime', timeString: string) => {
    // 1. Handle empty input (clear the value)
    if (!timeString) {
      onChange(field, null);
      return;
    }

    // 2. Parse the HH:mm string
    const [hoursStr, minutesStr] = timeString.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (isNaN(hours) || isNaN(minutes)) {
      return;
    }

    const newMinutes = hours * 60 + minutes;

    // 3. Handle startTime update
    if (field === 'startTime') {
      const baseDate = startTime || dayjs();
      const newStartTime = baseDate.hour(hours).minute(minutes).second(0);
      onChange('startTime', newStartTime);

      if (endTime) {
        const endMinutes = minutesOfDayFromDateTime(endTime);
        if (endMinutes === newMinutes) {
          onChange('endTime', null);
        } else if (endMinutes > newMinutes) {
          // Same day shift
          onChange('endTime', newStartTime.hour(endTime.hour()).minute(endTime.minute()).second(0));
        } else {
          // Overnight shift ending next day
          onChange('endTime', newStartTime.add(1, 'day').hour(endTime.hour()).minute(endTime.minute()).second(0));
        }
      }
      return;
    }

    // 4. Handle endTime update
    if (startTime) {
      const startMinutes = minutesOfDayFromDateTime(startTime);
      if (newMinutes === startMinutes) {
        return;
      }
      const isNextDay = newMinutes < startMinutes;
      const targetDate = isNextDay ? startTime.add(1, 'day') : startTime;
      const newEndTime = targetDate.hour(hours).minute(minutes).second(0);
      onChange('endTime', newEndTime);
      return;
    }

    const baseDate = endTime || dayjs();
    onChange('endTime', baseDate.hour(hours).minute(minutes).second(0));
  };

  return (
    <Grid container size={12} spacing={2}>
      <Grid size={6}>
        <TimeAutocomplete
          label="Check In Time"
          // Convert Dayjs -> String for the input
          time={startTime ? startTime.format('HH:mm') : ''}
          // Convert String -> Dayjs logic via handler
          onTimeChange={(newVal) => handleTimeUpdate('startTime', newVal)}
          error={!startTime}
          helperText={!startTime ? 'Start Time is required' : ''}
          {...(size && { size })}
        />
      </Grid>
      <Grid size={6}>
        <TimeAutocomplete
          label="Check Out Time"
          // Convert Dayjs -> String for the input
          time={endTime ? endTime.format('HH:mm') : ''}
          // Convert String -> Dayjs logic via handler
          onTimeChange={(newVal) => handleTimeUpdate('endTime', newVal)}
          options={endTimeOptions}
          formatOptionLabel={formatEndTimeLabel}
          error={!endTime || isRangeInverted}
          helperText={
            !endTime
              ? 'End Time must be valid'
              : isOvernight
                ? `Ends next day (${endTime.format(dateTimePreference.date)})`
                : ''
          }
          {...(size && { size })}
        />
      </Grid>
    </Grid>
  );
};

export default TimeScheduleSection;
